import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { pickLocale, formatUsd } from '@juvo/utils';
import type { Course, DonationCampaign, ImpactSnapshot, MerchProduct } from '@juvo/schemas';
import { store } from '../store.js';
import { publicSectionStyles } from '../styles/public.js';
import '../layouts/site-chrome.js';

const FEATURED = [
  'litteratie-numerique-cliniciens',
  'openmrs-administration',
  'maintenance-terrain-1',
];

const HERO_IMG =
  'https://images.unsplash.com/photo-1584515933487-779824d29309?q=80&w=1200&auto=format&fit=crop';
const TECH_IMG =
  'https://images.unsplash.com/photo-1517420704952-d9f39e95b43e?q=80&w=900&auto=format&fit=crop';
const FUNDS_IMG =
  'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?q=80&w=900&auto=format&fit=crop';
const IMPACT_IMG =
  'https://images.unsplash.com/photo-1584982751601-97dcc096659c?q=80&w=900&auto=format&fit=crop';

@customElement('jv-home-page')
export class JvHomePage extends LitElement {
  static styles = [
    publicSectionStyles,
    css`
      section {
        padding: 1.4rem 0 2.4rem;
      }
      .hero {
        display: grid;
        gap: 2rem;
        align-items: center;
        padding: 1.5rem 0 2.5rem;
      }
      @media (min-width: 1080px) {
        .hero {
          grid-template-columns: 1fr 1fr;
        }
      }
      h1 {
        font-size: var(--jv-fs-display);
        line-height: 1.15;
        margin: 0.55rem 0 1rem;
      }
      h1 .hl {
        color: var(--jv-color-amber);
      }
      .lede {
        font-size: 0.9rem;
        color: var(--jv-color-muted);
        max-width: 26rem;
        margin: 0 0 1.4rem;
      }
      .hero-img {
        position: relative;
        border: var(--jv-border-w) solid var(--jv-color-border);
        box-shadow: var(--jv-shadow-lg);
      }
      .hero-img img {
        width: 100%;
        height: 280px;
        object-fit: cover;
        filter: grayscale(15%);
      }
      .hero-badge {
        position: absolute;
        bottom: -14px;
        left: -14px;
        background: var(--jv-color-ink);
        color: #fff;
        font-size: 0.7rem;
        padding: 0.5rem 0.75rem;
        border: var(--jv-border-w) solid var(--jv-color-border);
      }
      .svc {
        background: var(--jv-color-surface);
        border: var(--jv-border-w) solid var(--jv-color-border);
        box-shadow: var(--jv-shadow);
        padding: 1.4rem 1rem;
        text-align: center;
        font-size: 0.85rem;
      }
      .svc .icon {
        width: 40px;
        height: 40px;
        margin: 0 auto 0.75rem;
        border: var(--jv-border-w) solid var(--jv-color-border);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        color: var(--jv-color-accent);
      }
      .impact {
        background: var(--jv-color-ink);
        color: #fff;
        display: grid;
        border: var(--jv-border-w) solid var(--jv-color-border);
      }
      @media (min-width: 800px) {
        .impact {
          grid-template-columns: 1fr 1fr;
        }
      }
      .impact-text {
        padding: 2.6rem;
      }
      .impact-text .tag {
        color: var(--jv-color-amber);
        margin-bottom: 0.7rem;
      }
      .impact-text h3 {
        font-size: 0.95rem;
        font-weight: 400;
        color: #c9cdc9;
        margin: 0 0 0.35rem;
      }
      .impact-num {
        font-size: 2.2rem;
        color: var(--jv-color-amber);
        font-weight: 700;
        margin: 0 0 1rem;
      }
      .impact img {
        width: 100%;
        height: 100%;
        min-height: 220px;
        object-fit: cover;
        filter: grayscale(40%) contrast(1.1);
      }
      .donation-grid {
        display: grid;
        gap: 1.2rem;
        align-items: stretch;
      }
      @media (min-width: 860px) {
        .donation-grid {
          grid-template-columns: 1.3fr 1fr;
        }
      }
      .stat-row {
        display: flex;
        gap: 1.4rem;
        margin: 0.4rem 0 0.4rem;
      }
      .stat-num {
        font-size: 1.35rem;
        font-weight: 700;
        color: var(--jv-color-accent);
      }
      .stat-label {
        font-size: 0.7rem;
        color: var(--jv-color-muted);
      }
      .funds-num {
        font-size: 1.85rem;
        font-weight: 700;
        color: var(--jv-color-accent);
        margin: 0;
      }
      .cta {
        text-align: center;
        padding: 3.2rem 0 1rem;
      }
      .cta h2 {
        font-size: 1.65rem;
        margin: 0 0 1.4rem;
        white-space: pre-line;
      }
      .lead-badge {
        position: absolute;
        top: 1.4rem;
        right: 1.4rem;
        background: var(--jv-color-amber);
        color: var(--jv-color-ink);
        font-size: 0.65rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        padding: 0.25rem 0.6rem;
        border: 1.5px solid var(--jv-color-border);
      }
      .relative {
        position: relative;
      }
      .amounts {
        display: flex;
        flex-wrap: wrap;
        gap: 0.45rem;
      }
      .amt {
        font-family: inherit;
        font-size: 0.75rem;
        border: var(--jv-border-w) solid var(--jv-color-border);
        background: var(--jv-color-surface);
        padding: 0.45rem 0.8rem;
        cursor: pointer;
      }
      .amt:hover,
      .amt[aria-pressed='true'] {
        background: var(--jv-color-accent);
        color: #fff;
      }
    `,
  ];

  @state() private courses: Course[] = [];
  @state() private merch: MerchProduct[] = [];
  @state() private campaign: DonationCampaign | null = null;
  @state() private impact: ImpactSnapshot | null = null;
  @state() private addedSku = '';

  connectedCallback() {
    super.connectedCallback();
    this.unsubscribe = store.subscribe(() => this.requestUpdate());
    store.client.getCourses({ locale: store.locale }).then((courses) => {
      this.courses = courses;
    });
    store.client.getMerch().then((merch) => {
      this.merch = merch;
    });
    store.client.getDonationCampaign().then((campaign) => {
      this.campaign = campaign;
    });
    store.client.getImpact().then((impact) => {
      this.impact = impact;
    });
  }
  disconnectedCallback() {
    this.unsubscribe?.();
    super.disconnectedCallback();
  }
  private unsubscribe?: () => void;

  private featured() {
    const picked = FEATURED.map((slug) => this.courses.find((course) => course.slug === slug)).filter(
      (course): course is Course => !!course,
    );
    return picked.length ? picked : this.courses.slice(0, 3);
  }

  render() {
    const t = store.t.bind(store);
    const campaign = this.campaign;
    const jvc = this.impact?.jvcIssued ?? 1_240_600;
    return html`
      <jv-page>
        <section class="hero">
          <div>
            <div class="tag dollar">${t('home.kicker')}</div>
            <h1>${t('home.hero')} <span class="hl">${t('home.heroHighlight')}</span></h1>
            <p class="lede">${t('home.lede')}</p>
            <jv-button href="/courses">${t('home.ctaCatalog')}</jv-button>
          </div>
          <div class="hero-img">
            <img src=${HERO_IMG} alt=${t('home.heroAlt')} />
            <div class="hero-badge">${t('home.heroBadge')}</div>
          </div>
        </section>

        <section>
          <div class="section-head">
            <div class="tag hash">${t('home.pillarsTag')}</div>
            <h2 class="section-title">${t('home.pillars')}</h2>
          </div>
          <div class="cards four">
            ${[
              ['01', t('home.pillar1')],
              ['02', t('home.pillar2')],
              ['03', t('home.pillar3')],
              ['04', t('home.pillar4')],
            ].map(
              ([n, label]) => html`
                <div class="svc">
                  <div class="icon">${n}</div>
                  ${label}
                </div>
              `,
            )}
          </div>
        </section>

        <section>
          <div class="section-head">
            <div class="tag hash">${t('home.catalogTag')}</div>
            <h2 class="section-title">${t('home.catalogTitle')}</h2>
          </div>
          <div class="cards thirds">
            ${this.featured().map((course) => this.courseCard(course))}
          </div>
        </section>

        <section>
          <div class="impact">
            <div class="impact-text">
              <div class="tag tilde">${t('home.impactTag')}</div>
              <h3>${t('home.impactLabel')}</h3>
              <p class="impact-num">${jvc.toLocaleString(store.locale === 'fr' ? 'fr-ML' : 'en-US')}</p>
              <jv-progress
                tone="dark"
                .value=${68}
                .max=${100}
              ></jv-progress>
              <p style="margin: 1.2rem 0 0">
                <jv-button href="/impact">${t('home.impactCta')}</jv-button>
              </p>
            </div>
            <img src=${IMPACT_IMG} alt="" />
          </div>
        </section>

        <section>
          <div class="section-head">
            <div class="tag hash">${t('home.eventsTag')}</div>
            <h2 class="section-title">${t('home.eventsTitle')}</h2>
          </div>
          <div class="cards thirds">
            ${this.featured().map((course) => this.eventCard(course))}
          </div>
        </section>

        <section>
          <div class="section-head">
            <div class="tag hash">${t('home.donateTag')}</div>
            <h2 class="section-title">${t('home.donateTitle')}</h2>
          </div>
          <p class="lede-center">${t('home.donateIntro')}</p>
          <div class="donation-grid">
            <jv-card lead image=${TECH_IMG} media="tall" alt="">
              <div class="relative">
                <div class="tag dollar">${t('donate.techTag')}</div>
                <span class="lead-badge">${t('donate.lead')}</span>
                <h3 class="card-title" style="font-size:1.25rem">${t('donate.techTitle')}</h3>
                <p class="muted">${t('donate.techBody')}</p>
                <div class="stat-row">
                  <div>
                    <div class="stat-num">${String(campaign?.kgRefurbished ?? 0).padStart(4, '0')}</div>
                    <div class="stat-label">${t('donate.kg')}</div>
                  </div>
                  <div>
                    <div class="stat-num">${String(campaign?.devicesThisQuarter ?? 0).padStart(4, '0')}</div>
                    <div class="stat-label">${t('donate.devices')}</div>
                  </div>
                </div>
                <jv-button href="/donate">${t('donate.pickup')}</jv-button>
              </div>
            </jv-card>
            <jv-card image=${FUNDS_IMG} media="tall" alt="">
              <div class="tag dollar">${t('donate.fundsTag')}</div>
              <h3 class="card-title" style="font-size:1.25rem">${t('donate.fundsTitle')}</h3>
              <p class="muted">${t('donate.fundsBody')}</p>
              <p class="funds-num">${formatUsd(campaign?.fundsRaisedUsd ?? 0, 0)}</p>
              <p class="muted">
                ${t('donate.raisedToward')} ${formatUsd(campaign?.fundsTargetUsd ?? 8000, 0)}
              </p>
              <jv-progress
                tone="amber"
                .value=${campaign?.fundsRaisedUsd ?? 0}
                .max=${campaign?.fundsTargetUsd ?? 8000}
              ></jv-progress>
              <div class="amounts">
                ${[10, 25, 50].map((amt) => html`<a class="amt" href=${`/donate?amount=${amt}`}>$${amt}</a>`)}
                <a class="amt" href="/donate">${t('donate.custom')}</a>
              </div>
              <p><jv-button href="/donate">${t('donate.now')}</jv-button></p>
            </jv-card>
          </div>
        </section>

        <section>
          <div class="section-head">
            <div class="tag hash">${t('home.merchTag')}</div>
            <h2 class="section-title">${t('home.merchTitle')}</h2>
          </div>
          <p class="lede-center">${t('home.merchIntro')}</p>
          <div class="cards thirds">
            ${this.merch.map((product) => this.merchCard(product))}
          </div>
        </section>

        <section class="cta">
          <div class="script">${t('home.ctaGeekTag')}</div>
          <h2>${t('home.ctaGeekTitle')}</h2>
          <jv-button href="/geeks">${t('home.ctaGeek')}</jv-button>
        </section>
      </jv-page>
    `;
  }

  private courseCard(course: Course) {
    const upcoming = course.upcoming;
    return html`
      <a class="plain" href="/courses/${course.slug}">
        <jv-card image=${course.heroImage ?? ''} alt="" media=${course.heroImage ? 'default' : ''}>
          <jv-badge>${course.code ?? course.track}</jv-badge>
          <h3 class="card-title">${pickLocale(course.title, store.locale)}</h3>
          <p class="muted">${pickLocale(course.description, store.locale)}</p>
          ${upcoming
            ? html`<jv-progress
                  .value=${upcoming.seatsTaken}
                  .max=${upcoming.capacity}
                ></jv-progress>
                <div class="card-meta">
                  <span>${upcoming.seatsTaken}/${upcoming.capacity} ${store.t('course.seats')}</span>
                  <span>${upcoming.startDate}</span>
                </div>`
            : ''}
        </jv-card>
      </a>
    `;
  }

  private eventCard(course: Course) {
    const date = course.upcoming?.startDate?.replace(/-/g, '.') ?? '';
    return html`
      <a class="plain" href="/courses/${course.slug}">
        <jv-card image=${course.heroImage ?? ''} alt="">
          <div class="tag">${date}</div>
          <h3 class="card-title">${pickLocale(course.title, store.locale)}</h3>
          <p class="muted">${course.upcoming?.location ?? course.location}</p>
        </jv-card>
      </a>
    `;
  }

  private merchCard(product: MerchProduct) {
    return html`
      <jv-card image=${product.image} alt=${pickLocale(product.name, store.locale)}>
        <jv-badge>${product.sku}</jv-badge>
        <h3 class="card-title">${pickLocale(product.name, store.locale)}</h3>
        <p class="muted">${pickLocale(product.description, store.locale)}</p>
        <div class="card-meta">
          <span class="price">${formatUsd(product.priceUsd)}</span>
          <jv-button size="small" variant="ghost" @click=${() => this.addMerch(product.sku)}>
            ${this.addedSku === product.sku ? store.t('merch.added') : store.t('merch.add')}
          </jv-button>
        </div>
      </jv-card>
    `;
  }

  private addMerch(sku: string) {
    store.addToCart(sku);
    this.addedSku = sku;
  }
}
